"use client";

import clsx from "clsx";
import type { ReactNode } from "react";
import Icon from "@/components/library/Icon";

type JotFormCalloutTone = "amber" | "red";

/** The tints live here and nowhere else, so the two tones stay in step. */
const TONE_CLASSES: Record<
  JotFormCalloutTone,
  { box: string; text: string; icon: string }
> = {
  amber: {
    box: "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800",
    text: "text-amber-700 dark:text-amber-300",
    icon: "text-amber-600 dark:text-amber-400",
  },
  red: {
    box: "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800",
    text: "text-red-700 dark:text-red-300",
    icon: "text-red-600 dark:text-red-400",
  },
};

interface JotFormCalloutBaseProps {
  tone: JotFormCalloutTone;
  message: string;
  testId?: string;
  /** Anything that belongs under the message — a button, copy rows. */
  children?: ReactNode;
}

/**
 * The dismiss button is icon-only, so its accessible name comes from
 * `dismissLabel` alone. Paired in the type rather than gated at render time: a
 * caller that wires up `onDismiss` and forgets the label fails to compile
 * instead of shipping a button screen readers announce as nothing.
 */
type JotFormCalloutDismissProps =
  | { onDismiss: () => void; dismissLabel: string }
  | { onDismiss?: never; dismissLabel?: never };

type JotFormCalloutProps = JotFormCalloutBaseProps & JotFormCalloutDismissProps;

/** A warning box: icon, message, optional dismiss, optional body. */
export default function JotFormCallout({
  tone,
  message,
  onDismiss,
  dismissLabel,
  testId,
  children,
}: Readonly<JotFormCalloutProps>) {
  const classes = TONE_CLASSES[tone];

  return (
    <div
      className={clsx("space-y-3 p-3 rounded border text-xs", classes.box)}
      data-testid={testId}
    >
      <div className="flex items-start gap-2">
        <Icon icon="triangle-exclamation" className={clsx("mt-0.5", classes.icon)} />
        <p className={clsx("flex-1", classes.text)}>{message}</p>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label={dismissLabel}
            className={classes.text}
          >
            <Icon icon="xmark" />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
